/**
 * Dates civiles et conversions de fuseau, sans dépendance externe.
 *
 * Un calendrier de garde se raisonne en **heure locale** : « vendredi 16h30, sortie
 * d'école » ne veut rien dire en UTC. Mais les instants stockés et comparés doivent être
 * absolus. Ce module fait le pont, et traite explicitement les deux jours de l'année où
 * la correspondance n'est pas bijective — passage à l'heure d'été et à l'heure d'hiver.
 */
export const DEFAULT_TIME_ZONE = 'Europe/Paris';
export const WEEKDAYS = {
    lundi: 1,
    mardi: 2,
    mercredi: 3,
    jeudi: 4,
    vendredi: 5,
    samedi: 6,
    dimanche: 7,
};
const MS_PER_DAY = 86_400_000;
/** Repère UTC d'une date civile : sert d'arithmétique calendaire, jamais d'instant. */
const utcOf = (date) => Date.UTC(date.year, date.month - 1, date.day);
export function plainDateToIso(date) {
    const mm = String(date.month).padStart(2, '0');
    const dd = String(date.day).padStart(2, '0');
    return `${date.year}-${mm}-${dd}`;
}
export function plainDateFromIso(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match)
        throw new TypeError(`Date civile invalide : ${iso}`);
    const [, y, m, d] = match;
    const date = { year: Number(y), month: Number(m), day: Number(d) };
    if (plainDateToIso(normalizeDate(date)) !== iso) {
        throw new TypeError(`Date civile inexistante : ${iso}`);
    }
    return date;
}
/** Ramène une date civile à sa forme canonique (31 février → 2 ou 3 mars). */
function normalizeDate(date) {
    const stamp = new Date(utcOf(date));
    return {
        year: stamp.getUTCFullYear(),
        month: stamp.getUTCMonth() + 1,
        day: stamp.getUTCDate(),
    };
}
export function addDays(date, days) {
    const stamp = new Date(utcOf(date) + days * MS_PER_DAY);
    return {
        year: stamp.getUTCFullYear(),
        month: stamp.getUTCMonth() + 1,
        day: stamp.getUTCDate(),
    };
}
/** Nombre de jours civils entre deux dates (positif si `to` est postérieure). */
export function daysBetween(from, to) {
    return Math.round((utcOf(to) - utcOf(from)) / MS_PER_DAY);
}
export function weekdayOf(date) {
    const day = new Date(utcOf(date)).getUTCDay();
    // getUTCDay : 0 = dimanche. La norme ISO place le dimanche en 7.
    return (day === 0 ? 7 : day);
}
export function comparePlainDate(a, b) {
    return utcOf(a) - utcOf(b);
}
/** Décalage du fuseau, en millisecondes, à un instant donné. */
function zoneOffsetMs(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const read = (type) => Number(parts.find((part) => part.type === type)?.value ?? '0');
    const asIfUtc = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'), read('second'));
    return asIfUtc - instant.getTime();
}
/**
 * Heure locale → instant absolu.
 *
 * Deux jours par an, la correspondance n'est pas bijective, et il faut trancher :
 *
 *   - **printemps** (2h → 3h) : l'heure locale demandée n'existe pas. On décale vers
 *     l'avant de la durée du saut — un échange prévu à 2h30 a lieu à 3h30, comme le font
 *     les agendas grand public ;
 *   - **automne** (3h → 2h) : elle existe deux fois. On retient la **première**, celle qui
 *     précède le recul : l'échange a lieu avant le changement, pas une heure après.
 *
 * L'algorithme échantillonne le décalage la veille et le lendemain, construit les instants
 * candidats, et ne garde que ceux qui redonnent bien l'heure locale demandée. Zéro candidat
 * signale un trou, deux candidats une heure doublée.
 */
export function wallTimeToInstant(date, time, timeZone = DEFAULT_TIME_ZONE) {
    const wallAsUtc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute);
    const offsetBefore = zoneOffsetMs(new Date(wallAsUtc - MS_PER_DAY), timeZone);
    const offsetAfter = zoneOffsetMs(new Date(wallAsUtc + MS_PER_DAY), timeZone);
    const candidates = [...new Set([offsetBefore, offsetAfter])]
        .map((offset) => new Date(wallAsUtc - offset))
        .filter((instant) => zoneOffsetMs(instant, timeZone) === wallAsUtc - instant.getTime());
    if (candidates.length === 0) {
        // Trou de printemps : le décalage d'avant la transition place l'instant après le saut.
        return new Date(wallAsUtc - Math.min(offsetBefore, offsetAfter));
    }
    // Heure doublée : la première occurrence est celle du plus grand décalage.
    return candidates.reduce((earliest, candidate) => candidate.getTime() < earliest.getTime() ? candidate : earliest);
}
/** Instant absolu → date et heure locales. */
export function instantToWallTime(instant, timeZone = DEFAULT_TIME_ZONE) {
    const shifted = new Date(instant.getTime() + zoneOffsetMs(instant, timeZone));
    const date = {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
    };
    return {
        date,
        time: { hour: shifted.getUTCHours(), minute: shifted.getUTCMinutes() },
        weekday: weekdayOf(date),
    };
}
//# sourceMappingURL=time-zone.js.map